import React, {FocusEvent, useState} from 'react';
import classNames from 'classnames';
import {translate} from 'helpers/localize';
import {useFormContext} from 'react-hook-form';
import InfoPopover from 'components/ui/InfoPopover/InfoPopover';
import {getDeepValue} from 'helpers/common';
import Alert from 'components/ui/Alert/Alert';
import Collapse from 'components/ui/Collapse/Collapse';
import ErrorFeedback from '../ErrorFeedback/ErrorFeedback';
import {IDefaultControlProps} from '../Form/Form';
import emailDomains from './email_domains.json';

import styles from './BusinessEmailInput.module.scss';

const BusinessEmailInput = (props: IDefaultControlProps) => {
    const {
        name,
        label,
        placeholder,
        className,
        info,
        disabled,
        readOnly,
        size,
        variant,
        rounded = true,
        registerOptions,
        onBlur,
    } = props;
    const [open, setOpen] = useState<boolean>(false);
    const {register, errors, setValue} = useFormContext();

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        const value = e.target.value.trim();
        setValue(name, value);
        if (!value) return;
        const domain = value.substring(value.lastIndexOf('@') + 1);
        if (!domain) return;

        if (emailDomains[domain as keyof typeof emailDomains] !== undefined) {
            setOpen(true);
        } else {
            setOpen(false);
        }

        if (onBlur) onBlur(e);
    };

    return (
        <>
            <div className={classNames(styles.container, className)}>
                <label htmlFor={name} className={styles.label}>
                    {label}
                    {info ? <InfoPopover content={info} /> : null}
                </label>
                <input
                    className={classNames(styles.input, className, {
                        [styles.invalid]: getDeepValue(errors, name),
                        [styles.dark]: variant === 'dark',
                        [styles.rounded]: rounded,
                        [styles.large]: size === 'large',
                        [styles.disabled]: disabled || readOnly,
                    })}
                    name={name}
                    type="email"
                    autoComplete="email"
                    ref={register(registerOptions)}
                    placeholder={translate(placeholder)}
                    disabled={disabled}
                    readOnly={readOnly}
                    data-notranslate
                    onBlur={handleBlur}
                    onChange={() => setOpen(false)}
                />
                {getDeepValue(errors, name)?.message ? (
                    <ErrorFeedback
                        message={getDeepValue(errors, name).message}
                    />
                ) : null}
            </div>
            <Collapse on={open}>
                <Alert
                    message="amnis provides international banking services to companies.
                Please note that we only onboard private individuals with a
                minimum yearly fx volume of EUR 100k."
                    variant="warning"
                />
            </Collapse>
        </>
    );
};

export default BusinessEmailInput;
